import { PrincipalTypeEnum } from '@ulysses-inc/harami_api_client'
import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import { localStorageKeys } from '~/adapter/localstorage/keys'
import { isNullish } from '~/utils/isNullish'
import type {
  AppRoleEnum,
  CompanyContractPlan,
} from '@ulysses-inc/harami_api_client'

type Company = {
  id: number
  name: string
  contractPlan?: CompanyContractPlan
}

/**
 * 認証主体 (Principal)。User / キオスク端末アカウント (Device) の両方を表す。
 *
 * 必要な情報があれば都度追加する。
 * ログイン時に様々な情報がAPIから返されるが、多くの情報をローカルストレージに保持すると変更が難しくなるので
 * 必要なもののみを入れたい。
 */
type Principal = {
  type: PrincipalTypeEnum
  /**
   * 認証主体の KRN（User なら members、Device なら devices 形式）。
   * NOTE: 旧形式 (user ベース) からの移行データは krn を持たないため空文字になりうる。
   * その場合は次回オンライン時の /me 取得で正しい値に上書きされる。
   */
  krn: string
  /** 表示名（User なら users.name、Device なら キオスク端末名）。 */
  name: string
  company: Company
  /** User 固有情報。Device 認証時は null。 */
  user: {
    uuid: string
    role: AppRoleEnum
  } | null
}

type SessionStoreState = {
  /**
   * 認証方法：Rumpログイン移行由来で認証方法を保持する形になっているが、今は Rump ログインに対応する rump_oidc 以外に存在しない。
   */
  authMethod: 'rump_oidc' | null
  principal: Principal | null
}

type SessionStoreAction = {
  rumpLogin: (principal: Principal) => void
  logout: () => void
}

/**
 * 永続化対象（関数を除いた state）。partialize / migrate の型として使う。
 */
type PersistedSession = Pick<SessionStoreState, 'authMethod' | 'principal'>

/**
 * version 0 の永続化データ形式。migrate で principal 形式へ変換する。
 * NOTE: version 0（リリース済み）のストアは User 経路のみで userRole を必ずセットしていたため、
 * userRole は非 null として扱う。
 */
type PersistedSessionV0 = {
  authMethod: 'rump_oidc' | null
  user: {
    uuid: string
    name: string
    company: Company
    userRole: AppRoleEnum
  } | null
}

/** 未ログイン（ログアウト）状態。不正・整合崩れの永続化データはここへ正規化する。 */
const LOGGED_OUT: PersistedSession = { authMethod: null, principal: null }

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

/**
 * version 0 の user 形状を検証する。
 * リリース済みの version 0 は User 経路のみで uuid / name / userRole / company を必ずセットしていたため、
 * これらが揃っていなければ壊れた / 改変されたデータとみなす。
 */
const isValidV0User = (
  value: unknown,
): value is NonNullable<PersistedSessionV0['user']> => {
  if (!isRecord(value)) return false
  const { company } = value
  return (
    typeof value.uuid === 'string' &&
    typeof value.name === 'string' &&
    typeof value.userRole === 'number' &&
    isRecord(company) &&
    typeof company.id === 'number' &&
    typeof company.name === 'string'
  )
}

/** User principal の user 属性（uuid / role）を検証する。null は許容しない。 */
const isValidUserAttributes = (user: unknown): boolean =>
  isRecord(user) &&
  typeof user.uuid === 'string' &&
  typeof user.role === 'number'

const isValidCompany = (company: unknown): boolean =>
  isRecord(company) &&
  typeof company.id === 'number' &&
  typeof company.name === 'string'

/**
 * principal (version 1) の形状を検証する。
 * user の要否は type で分岐する（User は uuid / role 必須、Device は null 必須）。
 * 未知の type は壊れた / 改変されたデータとみなして不正とする。
 */
const isValidPrincipal = (value: unknown): value is Principal => {
  if (!isRecord(value)) return false
  if (
    typeof value.krn !== 'string' ||
    typeof value.name !== 'string' ||
    !isValidCompany(value.company)
  ) {
    return false
  }
  switch (value.type) {
    case PrincipalTypeEnum.User:
      return isValidUserAttributes(value.user)
    case PrincipalTypeEnum.Device:
      return value.user === null
    default:
      return false
  }
}

/**
 * authMethod / principal の整合を取った PersistedSession を返す。
 * 認証済み (rump_oidc) かつ principal が妥当な場合のみログイン状態を維持し、
 * それ以外（不正な形状・整合崩れ）は未ログイン状態へ倒す。
 * これにより「isAuthed が true なのに principal が無い」不整合を防ぐ。
 *
 * NOTE: zustand の persist は version 不一致時しか migrate を呼ばないため、
 * 通常フロー（保存データが既に version 1）の正規化は persist の merge から本関数を呼んで行う。
 */
export const normalizePersistedSession = (
  candidate: unknown,
): PersistedSession => {
  if (!isRecord(candidate)) return LOGGED_OUT
  if (candidate.authMethod !== 'rump_oidc') return LOGGED_OUT
  if (!isValidPrincipal(candidate.principal)) return LOGGED_OUT
  return { authMethod: 'rump_oidc', principal: candidate.principal }
}

/**
 * 永続化データのバージョン移行。
 * version 0 (user ベース) → version 1 (principal ベース)。
 * 移行前のセッションはすべて User のため、type は USER 固定でマッピングする。
 * オフライン時は /me を叩かず永続化データのみを使うため、ここで確実に変換しておく必要がある。
 *
 * localStorage が壊れている / 手動で書き換えられている等で想定外の形だった場合は、
 * rehydrate 時に例外を投げず未ログイン状態へ正規化する（型アサーション前提の直接アクセスを避ける）。
 *
 * NOTE: persist は version 不一致時しか本関数を呼ばないため、ここで扱うのは version 0 からの移行
 * （および将来バージョンからのダウングレード等の想定外ケース）のみ。通常フロー（version 1 データ）の
 * 形状・整合検証は persist の merge で行う。
 *
 * @param persisted 永続化された state
 * @param fromVersion 永続化データのバージョン
 * @returns principal 形式の state
 */
export const migrateSessionStore = (
  persisted: unknown,
  fromVersion: number,
): PersistedSession => {
  if (fromVersion === 0) {
    // user 不在 / 形状不正は未ログイン状態へ。
    if (!isRecord(persisted) || !isValidV0User(persisted.user)) {
      return LOGGED_OUT
    }
    const { user } = persisted
    // 妥当な user が存在する = ログイン済みなので authMethod を rump_oidc に確定し、
    // isAuthed と principal の整合を保つ。
    return {
      authMethod: 'rump_oidc',
      principal: {
        type: PrincipalTypeEnum.User,
        // 旧データは krn を持たないため空文字。次回オンラインの /me 取得で埋まる。
        krn: '',
        name: user.name,
        company: user.company,
        user: {
          uuid: user.uuid,
          role: user.userRole,
        },
      },
    }
  }
  // version 1 以降（および想定外のバージョン）。形状・整合を検証し、不正なら未ログイン状態へ倒す。
  return normalizePersistedSession(persisted)
}

export const useSessionStore = create<SessionStoreState & SessionStoreAction>()(
  devtools(
    immer(
      persist(
        set => ({
          authMethod: null,
          principal: null,
          rumpLogin: principal =>
            set(state => {
              state.authMethod = 'rump_oidc'
              state.principal = principal
            }),
          logout: () =>
            set(state => {
              state.authMethod = null
              state.principal = null
            }),
        }),
        {
          name: localStorageKeys.sessionStore,
          version: 1,
          // 関数を除いた永続化対象のみを保存する。これにより migrate / PersistedState の型も data のみになる。
          partialize: (state): PersistedSession => ({
            authMethod: state.authMethod,
            principal: state.principal,
          }),
          migrate: migrateSessionStore,
          // persist は version 不一致時しか migrate を呼ばないため、通常フロー（保存データが既に
          // version 1）でも形状・整合を検証できるよう merge で正規化する。これにより version 1 データが
          // 壊れていても「isAuthed が true なのに principal が無い」不整合を rehydrate 時に防ぐ。
          merge: (persisted, current) => ({
            ...current,
            ...normalizePersistedSession(persisted),
          }),
          onRehydrateStorage: () => {
            // beta キーを削除する。リリースして時間経過したらこの行を消すこと。
            localStorage.removeItem(localStorageKeys.sessionStore + '-beta')
          },
        },
      ),
    ),
  ),
)

/**
 * User の uuid を取得する。
 * store 内に User 情報が格納されていない場合（未ログイン / Device 認証）、エラーを throw する。
 *
 * @param state
 * @returns
 */
export const selectUserUuid = (state: SessionStoreState): string => {
  const uuid = state.principal?.user?.uuid
  if (!uuid) {
    throw new Error('User uuid not found')
  }
  return uuid
}

/**
 * 記録者（認証主体）を一意に識別する値を取得する。
 * User は user.uuid、Device は principal.krn（Device は user uuid を持たないため）。
 * User / Device いずれでも利用でき、未ログイン時のみ throw する。
 *
 * @param state
 * @returns
 */
export const selectReporterIdentifier = (state: SessionStoreState): string => {
  const id = state.principal?.user?.uuid ?? state.principal?.krn
  if (!id) {
    throw new Error('principal identifier not found')
  }
  return id
}

/**
 * 認証主体の表示名を取得する。
 * store 内に認証主体情報が格納されていない場合、エラーを throw する。
 *
 * @param state
 * @returns
 */
export const selectUserName = (state: SessionStoreState): string => {
  const name = state.principal?.name
  // 初期ユーザーなどだと空文字のケースもありうるため、空文字は許可
  if (isNullish(name)) {
    throw new Error('Principal not found')
  }
  return name
}

/**
 * 認証主体が所属する company_id を取得する。
 * store 内に企業情報が格納されていない場合、エラーを throw する。
 *
 * @param state
 * @returns
 */
export const selectUserCompanyId = (state: SessionStoreState): number => {
  const companyId = state.principal?.company.id
  // company_id = 0 のレコードは存在しないためエラーとして良い
  if (!companyId) {
    throw new Error('Principal not found')
  }
  return companyId
}

/**
 * 認証状態を取得する。
 *
 * @param state
 * @returns
 */
export const selectIsAuthed = (state: SessionStoreState): boolean => {
  return state.authMethod === 'rump_oidc'
}

/**
 * 認証主体の role が指定されたいづれかの role に該当する場合、true を返す。
 * 未ログイン（principal なし）／ Device 認証（user/role なし・RBAC 対象外）の場合は false。
 *
 * @param roles
 * @returns
 */
export const selectHasAnyUserRole =
  (roles: readonly AppRoleEnum[]) =>
  (state: SessionStoreState): boolean => {
    const role = state.principal?.user?.role
    if (role === undefined) return false
    return roles.includes(role)
  }

/**
 * 認証主体が所属する企業の契約プランを取得する。
 * undefined は企業情報が未取得の状態を表す。
 * 呼び出し側では undefined を非プレミアム扱いせず、未取得として扱うこと。
 */
export const selectContractPlan = (
  state: SessionStoreState,
): CompanyContractPlan | undefined => {
  return state.principal?.company.contractPlan
}
